import { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import API_URL from "@/utils/api";
import { toast } from "sonner";
import { resolveHostContext } from "../shell/useHostContext";

const API = API_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Configure axios to include token and tenant in requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // ── TENANT RESOLUTION ──────────────────────────────────────
  if (typeof window !== 'undefined') {
    const { subdomain } = resolveHostContext(
      window.location.hostname, 
      window.location.pathname, 
      window.location.search
    );
    
    if (subdomain) {
      config.params = { ...config.params, tenant: subdomain };
      config.headers['X-Tenant-Id'] = subdomain;
    }
  }
  
  // Always include credentials for cookie-based auth
  config.withCredentials = true;
  return config;
});

// Log API errors for debugging
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('[API Error]', error.config?.url, error.response?.status, error.message);
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize user state - check localStorage on mount
  const getInitialUser = () => {
    const token = localStorage.getItem("session_token");
    return token ? undefined : null; // null = definitely logged out, undefined = checking
  };

  const [user, setUser] = useState(getInitialUser);
  const [isLoading, setIsLoading] = useState(false);

  // DIRECT Google Login via Backend
  const login = useCallback(() => {
    // Construct the redirect URL for Google login
    // returnTo will preserve the active branch and table
    const currentUrl = window.location.href;
    window.location.href = `${API}/auth/google/start?returnTo=${encodeURIComponent(currentUrl)}`;
  }, []);

  // processSession is mainly for legacy demobackend, but we can keep it as a fallback
  // for manual session processing if ever needed.
  const processSession = useCallback(async (sessionId) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      setUser(response.data);
      if (response.data.access_token) {
        localStorage.setItem("session_token", response.data.access_token);
      }
      return response.data;
    } catch (error) {
      console.error("Session error:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async (tokenOverride = null) => {
    const token = tokenOverride || localStorage.getItem("session_token");
    if (!token) {
      // Don't call setUser unless we need to change it to null
      return false;
    }

    setIsLoading(true);
    console.log(`[Auth] Checking auth with token: ${token.substring(0, 10)}...`);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/auth/me`, {
        headers,
        withCredentials: true,
      });

      console.log(`[Auth] Auth success for user: ${response.data.email}`);
      setUser(response.data);
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      console.error(`[Auth] Auth check failed:`, error.response?.status, errorMsg);
      console.error(`[Auth] Detailed error response:`, error.response?.data);
      
      if (tokenOverride) {
        toast.error("Error al validar el acceso: " + errorMsg);
      }
      
      if (token) {
        setUser(null);
        localStorage.removeItem("session_token");
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []); // Remove user dependency to avoid loop

  const loginWithToken = useCallback(async (token) => {
    console.log(`[Auth] loginWithToken called with token: ${token.substring(0, 10)}...`);
    localStorage.setItem("session_token", token);
    // Explicitly update axios headers for safety
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    const result = await checkAuth(token);
    console.log(`[Auth] loginWithToken result: ${result}`);
    return result;
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    localStorage.removeItem("session_token");
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Handle successful login redirect (?success=true)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      // Remove the success param from URL without refreshing
      const newUrl = window.location.pathname + window.location.search.replace(/[?&]success=true/, '').replace(/^&/, '?') + window.location.hash;
      window.history.replaceState({}, '', newUrl);

      // Refresh user data
      checkAuth();
      toast.success("¡Sesión iniciada correctamente!");
    }
  }, [checkAuth]);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value = {
    user,
    setUser,
    isLoading,
    login,
    logout,
    loginWithToken,
    processSession,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};



